import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Bars, downloadCsv, Kpi, plain, Table } from './Reports.tsx';

/**
 * Budget: the year's budget in lines, what is committed and spent on each, and what is still
 * available. Prepared as a draft, approved once, then changed only by recorded changes.
 */

type YearState = 'draft' | 'approved' | 'closed';
type Control = 'off' | 'warn' | 'strict';
type CommitState = 'open' | 'closed' | 'cancelled';
interface YearRow {
  id: string;
  year: number;
  state: YearState;
  control: Control;
  note: string | null;
  payroll_line: string | null;
  current: number;
  spent: number;
  committed: number;
  lines: number;
}
export interface LineRow {
  id: string;
  code: string;
  name: string;
  group: string | null;
  original: number;
  changes: number;
  current: number;
  committed: number;
  spent: number;
  available: number;
  active: boolean;
}
type Sum = Record<'original' | 'changes' | 'current' | 'committed' | 'spent' | 'available', number>;
interface Overview {
  year: YearRow & { approved_at: string | null; approved_by_name: string | null; closed_at: string | null };
  lines: LineRow[];
  totals: Sum;
  months: { line: string; month: number; amount: number }[];
  changes: { id: string; number: number; from_line: string | null; to_line: string | null; amount: number; reason: string; date: string; by_name: string | null }[];
  can: { manage: boolean; approve: boolean };
  sources: { purchase: boolean; payroll: boolean };
}
interface CommitmentRow {
  id: string;
  name: string;
  line: string;
  line_code: string;
  line_name: string;
  date: string;
  description: string;
  party: string | null;
  amount: number;
  spent: number;
  state: CommitState;
  document: 'purchase.order' | 'purchase.bill' | 'payroll.run' | null;
}

type Tab = 'lines' | 'commitments' | 'changes';
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'lines', icon: 'list' },
  { key: 'commitments', icon: 'lock' },
  { key: 'changes', icon: 'transfer' },
];
const YEAR_STATE: Record<YearState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  approved: { tone: 'ok', icon: 'checkCircle' },
  closed: { tone: 'neutral', icon: 'lock' },
};
const COMMIT_STATE: Record<CommitState, { tone: Tone; icon: IconName }> = {
  open: { tone: 'info', icon: 'clock' },
  closed: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
const lineName = (l: { code: string; name: string }) => `${l.code} · ${l.name}`;

export function Budget() {
  const [params, setParams] = useSearchParams();
  const { data: list, reload: reloadYears } = useAction<{ years: YearRow[]; today: string }>('budget.years');
  const [creating, setCreating] = useState(false);
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'lines') as Tab;
  const thisYear = Number((list?.today ?? todayIso()).slice(0, 4));
  const chosen = list?.years.find((y) => y.id === params.get('year')) ?? list?.years.find((y) => y.year === thisYear) ?? list?.years[0] ?? null;
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };

  return (
    <Shell>
      <div className="page reports budget">
        <div className="page-head">
          <div>
            <h1>{t('bud.title')}</h1>
            <p className="muted">{t('bud.subtitle')}</p>
          </div>
          {list?.years.length ? (
            <div className="page-actions">
              <select className="input bud-year-pick" value={chosen?.id ?? ''} onChange={(e) => set({ year: e.target.value })} aria-label={t('bud.year')}>
                {list.years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {t('bud.year_n', { year: y.year })}
                  </option>
                ))}
              </select>
              <Button icon="plus" onClick={() => setCreating(true)}>
                {t('bud.new_year')}
              </Button>
            </div>
          ) : null}
        </div>
        {!list ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : !chosen ? (
          <EmptyState
            icon="bank"
            title={t('bud.empty')}
            body={t('bud.empty_body')}
            action={
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                {t('bud.new_year')}
              </Button>
            }
          />
        ) : (
          <YearView key={chosen.id} id={chosen.id} tab={tab} onTab={(k) => set({ tab: k === 'lines' ? null : k })} onYears={reloadYears} />
        )}
      </div>
      {creating && list ? (
        <NewYear
          years={list.years}
          suggested={list.years.some((y) => y.year === thisYear) ? Math.max(...list.years.map((y) => y.year)) + 1 : thisYear}
          onClose={() => setCreating(false)}
          onDone={async (id) => {
            setCreating(false);
            await reloadYears();
            set({ year: id, tab: null });
          }}
        />
      ) : null}
    </Shell>
  );
}

function YearView({ id, tab, onTab, onYears }: { id: string; tab: Tab; onTab: (t: Tab) => void; onYears: () => Promise<unknown> }) {
  const { can } = useSession();
  const { data, reload } = useAction<Overview>('budget.overview', { year: id });
  const { busy, run } = useSave();
  const [dialog, setDialog] = useState<null | 'settings' | 'change' | 'approve' | 'close' | 'commit' | { line: LineRow | null }>(null);
  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  const y = data.year;
  const T = data.totals;
  const changed = async () => {
    await reload();
    await onYears();
  };
  const st = YEAR_STATE[y.state];
  const approveOrClose = (what: 'approve' | 'close') =>
    void run(
      async () => {
        await action(what === 'approve' ? 'budget.approve_year' : 'budget.close_year', { year: y.id });
        setDialog(null);
        await changed();
      },
      what === 'approve' ? t('bud.approved_done') : t('bud.closed_done'),
    );
  const exportCsv = () =>
    downloadCsv(
      `budget-${y.year}.csv`,
      [t('bud.c_code'), t('bud.c_name'), t('bud.c_group'), t('bud.c_original'), t('bud.c_changes'), t('bud.c_current'), t('bud.c_committed'), t('bud.c_spent'), t('bud.c_available')],
      data.lines.map((l) => [l.code, l.name, l.group ?? '', plain(l.original), plain(l.changes), plain(l.current), plain(l.committed), plain(l.spent), plain(l.available)]),
    );

  return (
    <>
      <div className="bud-status">
        <StatusBadge tone={st.tone} icon={st.icon} label={t(`bud.st_${y.state}` as MessageKey)} />
        <span className="muted small">
          {y.state === 'draft'
            ? t('bud.draft_hint')
            : y.state === 'approved'
              ? [(y.approved_by_name ? t('bud.approved_on', { day: day(y.approved_at!.slice(0, 10)), who: y.approved_by_name }) : t('bud.approved_on_day', { day: day(y.approved_at!.slice(0, 10)) })), t(`bud.control_${y.control}_short` as MessageKey)].join(' · ')
              : t('bud.closed_hint')}
        </span>
        <span className="row-actions bud-status-actions">
          {data.can.manage && y.state !== 'closed' ? (
            <Button size="sm" variant="ghost" icon="settings" onClick={() => setDialog('settings')}>
              {t('bud.settings')}
            </Button>
          ) : null}
          {data.can.approve && y.state === 'draft' ? (
            <Button size="sm" variant="primary" icon="check" onClick={() => setDialog('approve')} disabled={!data.lines.length}>
              {t('bud.approve')}
            </Button>
          ) : null}
          {data.can.approve && y.state === 'approved' ? (
            <Button size="sm" variant="ghost" icon="lock" onClick={() => setDialog('close')}>
              {t('bud.close')}
            </Button>
          ) : null}
        </span>
      </div>
      {y.note ? <p className="muted bud-note">{y.note}</p> : null}

      <section className="rep-kpis" aria-label={t('bud.totals')}>
        <Kpi label={t('bud.k_current')} value={money(T.current)} hint={T.changes ? t('bud.k_changes', { amount: (T.changes > 0 ? '+' : '') + money(T.changes) }) : t('bud.k_as_approved')} />
        <Kpi label={t('bud.k_spent')} value={money(T.spent)} hint={t('bud.k_of_budget', { pct: pct(T.spent, T.current) })} />
        <Kpi label={t('bud.k_committed')} value={money(T.committed)} hint={t('bud.k_committed_hint')} />
        <Kpi label={t('bud.k_available')} value={money(T.available)} hint={T.available < 0 ? t('bud.k_over') : t('bud.k_of_budget', { pct: pct(T.available, T.current) })} warn={T.available < 0} />
      </section>

      <div className="segmented tabs" role="tablist" aria-label={t('bud.title')}>
        {TABS.map((x) => (
          <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => onTab(x.key)}>
            <Icon name={x.icon} size={16} />
            {t(`bud.tab_${x.key}` as MessageKey)}
          </button>
        ))}
      </div>

      {tab === 'lines' ? (
        <>
          <div className="toolbar">
            <span className="muted small">{tn('bud.n_lines', data.lines.length)}</span>
            <span className="row-actions">
              <Button size="sm" icon="download" onClick={exportCsv} disabled={!data.lines.length}>
                {t('bud.spreadsheet')}
              </Button>
              {data.can.approve && y.state === 'approved' ? (
                <Button size="sm" icon="transfer" onClick={() => setDialog('change')}>
                  {t('bud.change')}
                </Button>
              ) : null}
              {data.can.manage && y.state !== 'closed' ? (
                <Button size="sm" variant="primary" icon="plus" onClick={() => setDialog({ line: null })}>
                  {t('bud.add_line')}
                </Button>
              ) : null}
            </span>
          </div>
          <LinesTable data={data} onOpen={(l) => setDialog({ line: l })} />
          <MonthChart data={data} />
        </>
      ) : tab === 'commitments' ? (
        <Commitments data={data} canCommit={can('budget.commit') && y.state === 'approved'} onChange={changed} />
      ) : (
        <Changes data={data} />
      )}

      {dialog === 'settings' ? <YearSettings data={data} onClose={() => setDialog(null)} onDone={async () => (setDialog(null), await changed())} /> : null}
      {dialog === 'change' ? <ChangeDialog data={data} onClose={() => setDialog(null)} onDone={async () => (setDialog(null), await changed())} /> : null}
      {dialog && typeof dialog === 'object' ? (
        <LineDialog data={data} line={dialog.line} onClose={() => setDialog(null)} onDone={async () => (setDialog(null), await changed())} />
      ) : null}
      {dialog === 'approve' || dialog === 'close' ? (
        <Dialog
          title={dialog === 'approve' ? t('bud.approve_title', { year: y.year }) : t('bud.close_title', { year: y.year })}
          onClose={() => setDialog(null)}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" icon={dialog === 'approve' ? 'check' : 'lock'} onClick={() => approveOrClose(dialog)} busy={busy}>
                {dialog === 'approve' ? t('bud.approve') : t('bud.close')}
              </Button>
            </>
          }
        >
          <p>{dialog === 'approve' ? t('bud.approve_body', { amount: money(T.original), n: data.lines.length }) : t('bud.close_body', { amount: money(T.committed) })}</p>
        </Dialog>
      ) : null}
    </>
  );
}

/** The lines, in their groups, with a bar for how much of each is used. */
function LinesTable({ data, onOpen }: { data: Overview; onOpen: (l: LineRow) => void }) {
  if (!data.lines.length) return <EmptyState icon="list" title={t('bud.no_lines')} body={data.year.state === 'draft' ? t('bud.no_lines_body') : undefined} />;
  const T = data.totals;
  return (
    <Table
      view="budget.lines"
      head={[t('bud.c_line'), t('bud.c_original'), t('bud.c_changes'), t('bud.c_current'), t('bud.c_committed'), t('bud.c_spent'), t('bud.c_available'), t('bud.c_used')]}
      phoneHide={[1, 2, 3, 4, 5]}
      left={[0]}
      rows={data.lines.map((l) => ({
        key: l.id,
        onClick: () => onOpen(l),
        cells: [
          <span className="vat-party">
            <span>
              <span className="tabular strong">{l.code}</span> {l.name}
            </span>
            <span className="muted">{[l.group, l.active ? null : t('bud.not_in_use')].filter(Boolean).join(' · ') || ' '}</span>
          </span>,
          <span className="tabular">{money(l.original)}</span>,
          <span className={`tabular${l.changes ? '' : ' muted'}`}>{l.changes ? (l.changes > 0 ? '+' : '') + money(l.changes) : '—'}</span>,
          <span className="tabular">{money(l.current)}</span>,
          <span className="tabular">{money(l.committed)}</span>,
          <span className="tabular">{money(l.spent)}</span>,
          <span className={`tabular strong${l.available < 0 ? ' text-danger' : ''}`}>{money(l.available)}</span>,
          <UseBar l={l} />,
        ],
      }))}
      foot={[t('bud.total'), money(T.original), money(T.changes), money(T.current), money(T.committed), money(T.spent), money(T.available), <UseBar l={T} />]}
    />
  );
}

/** Spent, then committed, of the line's current budget. */
function UseBar({ l }: { l: { current: number; spent: number; committed: number } }) {
  const spent = pct(l.spent, l.current);
  const committed = pct(l.committed, l.current);
  const over = l.spent + l.committed > l.current;
  return (
    <span className={`bud-bar${over ? ' over' : ''}`} title={t('bud.bar_tip', { spent, committed })} aria-label={t('bud.bar_tip', { spent, committed })}>
      <span className="bud-bar-track">
        <span className="bud-bar-spent" style={{ width: `${Math.min(100, spent)}%` }} />
        <span className="bud-bar-committed" style={{ width: `${Math.max(0, Math.min(100 - Math.min(100, spent), committed))}%` }} />
      </span>
      <span className="tabular small">{Math.min(999, spent + committed)}%</span>
    </span>
  );
}

function MonthChart({ data }: { data: Overview }) {
  if (!data.months.length) return null;
  const per = Array.from({ length: 12 }, (_, i) => data.months.filter((m) => m.month === i + 1).reduce((s, m) => s + m.amount, 0));
  const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', timeZone: 'UTC' });
  return (
    <section className="panel rep-wide bud-months" aria-labelledby="bud-months">
      <div className="panel-head">
        <h2 id="bud-months">{t('bud.by_month')}</h2>
      </div>
      <Bars items={per.map((v, i) => { const label = fmt.format(new Date(Date.UTC(data.year.year, i, 1))); return { key: String(i), label, value: v, tip: `${label}: ${money(v)}` }; })} />
    </section>
  );
}

/* ---------------------------------------------------------------- Commitments */

function Commitments({ data, canCommit, onChange }: { data: Overview; canCommit: boolean; onChange: () => Promise<void> }) {
  const [state, setState] = useState<CommitState | 'all'>('open');
  const { data: rows, reload } = useAction<CommitmentRow[]>('budget.commitments', { year: data.year.id, state: state === 'all' ? null : state });
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const done = async () => {
    await reload();
    await onChange();
  };
  return (
    <>
      <div className="toolbar">
        <div className="segmented subtle" role="radiogroup" aria-label={t('bud.show')}>
          {(['open', 'closed', 'cancelled', 'all'] as const).map((s) => (
            <button key={s} role="radio" aria-checked={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
              {t(`bud.cs_${s}` as MessageKey)}
            </button>
          ))}
        </div>
        {canCommit ? (
          <Button size="sm" variant="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('bud.new_commitment')}
          </Button>
        ) : null}
      </div>
      {!rows ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : !rows.length ? (
        <EmptyState icon="lock" title={t('bud.no_commitments')} body={t('bud.no_commitments_body')} />
      ) : (
        <div className="list-card" role="list">
          {rows.map((c) => (
            <button key={c.id} role="listitem" className="list-row bud-commit-row" onClick={() => setOpen(c.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name={c.document === 'purchase.order' ? 'bag' : c.document === 'purchase.bill' ? 'invoice' : c.document === 'payroll.run' ? 'cash' : 'note'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {c.description}
                </span>
                <span className="list-sub">{[c.name, lineName({ code: c.line_code, name: c.line_name }), c.party, day(c.date)].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="bud-commit-end">
                <span className="tabular strong">{money(c.amount)}</span>
                <span className="muted small tabular">{t('bud.spent_of', { spent: money(c.spent) })}</span>
                <StatusBadge tone={COMMIT_STATE[c.state].tone} icon={COMMIT_STATE[c.state].icon} label={t(`bud.cs_${c.state}` as MessageKey)} />
              </span>
            </button>
          ))}
        </div>
      )}
      {adding ? <CommitDialog data={data} onClose={() => setAdding(false)} onDone={async () => (setAdding(false), await done())} /> : null}
      {open ? <CommitmentView id={open} canCommit={canCommit} onClose={() => setOpen(null)} onChange={done} /> : null}
    </>
  );
}

function CommitDialog({ data, onClose, onDone }: { data: Overview; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const usable = data.lines.filter((l) => l.active);
  const [line, setLine] = useState(usable[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [party, setParty] = useState('');
  const [date, setDate] = useState(todayIso().startsWith(String(data.year.year)) ? todayIso() : `${data.year.year}-01-01`);
  const [tried, setTried] = useState(false);
  const minor = parseMoney(amount);
  const chosen = usable.find((l) => l.id === line);
  const errors = { amount: minor === null || minor <= 0 ? t('bud.amount_wrong') : null, description: description.trim() ? null : t('form.required') };
  const over = chosen && minor !== null && minor > chosen.available;
  const save = () => {
    setTried(true);
    if (errors.amount || errors.description) return;
    void run(async () => {
      await action('budget.commit', { line, amount: minor, description: description.trim(), party: party.trim() || null, date });
      await onDone();
    }, t('bud.committed_done'));
  };
  return (
    <Dialog
      title={t('bud.new_commitment')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="lock" onClick={save} busy={busy}>
            {t('bud.commit')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('bud.commit_hint')}</p>
      <Field label={t('bud.c_line')} hint={chosen ? t('bud.available_now', { amount: money(chosen.available) }) : undefined}>
        <select className="input" value={line} onChange={(e) => setLine(e.target.value)} data-autofocus>
          {usable.map((l) => (
            <option key={l.id} value={l.id}>
              {lineName(l)}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('bud.what_for')} hint={t('bud.what_for_hint')} error={tried ? errors.description : null} required>
        <input className="input" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="form-row">
        <Field label={t('bud.amount')} error={tried ? errors.amount : null} required>
          <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
        </Field>
        <Field label={t('bud.date')}>
          <input className="input" type="date" value={date} min={`${data.year.year}-01-01`} max={`${data.year.year}-12-31`} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      <Field label={t('bud.party')} hint={t('bud.party_hint')}>
        <input className="input" maxLength={120} value={party} onChange={(e) => setParty(e.target.value)} />
      </Field>
      {over ? (
        <p className={`callout ${data.year.control === 'strict' ? 'callout-danger' : 'callout-warn'}`}>
          <Icon name="alert" size={16} />
          <span>{data.year.control === 'strict' ? t('bud.over_strict', { amount: money(chosen!.available) }) : t('bud.over_warn', { amount: money(chosen!.available) })}</span>
        </p>
      ) : null}
    </Dialog>
  );
}

interface CommitmentDetail {
  commitment: CommitmentRow & { record: string | null };
  line: LineRow;
  year: { year: number; state: YearState };
  spends: { id: string; date: string; amount: number; label: string; cancelled: boolean }[];
}

function CommitmentView({ id, canCommit, onClose, onChange }: { id: string; canCommit: boolean; onClose: () => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<CommitmentDetail>('budget.commitment', { id });
  const { busy, run } = useSave();
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const [tried, setTried] = useState(false);
  const c = data?.commitment;
  const open = c?.state === 'open' && canCommit;
  const done = async () => {
    await reload();
    await onChange();
  };
  const minor = parseMoney(amount);
  const pay = () => {
    setTried(true);
    if (minor === null || minor <= 0 || !label.trim()) return;
    void run(async () => {
      await action('budget.spend', { commitment: id, amount: minor, label: label.trim() });
      setPaying(false);
      setAmount('');
      setLabel('');
      setTried(false);
      await done();
    }, t('bud.spent_done'));
  };
  const release = (cancel: boolean) =>
    void run(async () => {
      await action('budget.release', { commitment: id, cancel });
      await done();
    }, cancel ? t('bud.cancelled_done') : t('bud.released_done'));
  return (
    <Dialog
      title={c ? `${c.name} · ${c.description}` : t('bud.commitment')}
      onClose={onClose}
      footer={
        open && c ? (
          paying ? (
            <>
              <Button variant="ghost" onClick={() => setPaying(false)}>
                {t('common.back')}
              </Button>
              <Button variant="primary" icon="check" onClick={pay} busy={busy}>
                {t('bud.record_spend')}
              </Button>
            </>
          ) : (
            <>
              <span className="foot-actions">
                {c.spent === 0 ? (
                  <Button variant="ghost" icon="x" onClick={() => release(true)} disabled={busy}>
                    {t('bud.cancel_commitment')}
                  </Button>
                ) : (
                  <Button variant="ghost" icon="undo" onClick={() => release(false)} disabled={busy}>
                    {t('bud.release_rest')}
                  </Button>
                )}
              </span>
              {!c.document ? (
                <Button variant="primary" icon="cash" onClick={() => setPaying(true)}>
                  {t('bud.record_spend')}
                </Button>
              ) : null}
            </>
          )
        ) : (
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        )
      }
    >
      {!data || !c ? (
        <Skeleton h={160} />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('bud.committed_amount')}</span>
              <strong>{money(c.amount)}</strong>
            </div>
            <div>
              <span className="muted">{t('bud.c_spent')}</span>
              <strong>{money(c.spent)}</strong>
            </div>
            <div>
              <span className="muted">{t('bud.left')}</span>
              <strong>{money(Math.max(0, c.amount - c.spent))}</strong>
            </div>
            <StatusBadge tone={COMMIT_STATE[c.state].tone} icon={COMMIT_STATE[c.state].icon} label={t(`bud.cs_${c.state}` as MessageKey)} />
          </div>
          <dl className="appr-facts">
            <dt>{t('bud.c_line')}</dt>
            <dd>{lineName(data.line)}</dd>
            {c.party ? (
              <>
                <dt>{t('bud.party')}</dt>
                <dd>{c.party}</dd>
              </>
            ) : null}
            <dt>{t('bud.date')}</dt>
            <dd>{day(c.date)}</dd>
            {c.document ? (
              <>
                <dt>{t('bud.from_document')}</dt>
                <dd>{t(`bud.doc_${c.document.replace('.', '_')}` as MessageKey)}</dd>
              </>
            ) : null}
          </dl>
          {paying ? (
            <div className="form-row">
              <Field label={t('bud.spend_label')} hint={t('bud.spend_label_hint')} error={tried && !label.trim() ? t('form.required') : null} required>
                <input className="input" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} data-autofocus />
              </Field>
              <Field label={t('bud.amount')} error={tried && (minor === null || minor <= 0) ? t('bud.amount_wrong') : null} required>
                <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(Math.max(0, c.amount - c.spent) / 100)} />
              </Field>
            </div>
          ) : null}
          <section className="item-moves">
            <h3>{t('bud.spending')}</h3>
            {data.spends.length ? (
              <div className="list-card">
                {data.spends.map((s) => (
                  <div key={s.id} className={`list-row compact-move${s.cancelled ? ' archived' : ''}`}>
                    <span className="list-icon" aria-hidden="true">
                      <Icon name={s.cancelled ? 'x' : 'cash'} size={18} />
                    </span>
                    <span className="list-main">
                      <span className="list-title">{s.label}</span>
                      <span className="list-sub">{[day(s.date), s.cancelled ? t('bud.cancelled') : null].filter(Boolean).join(' · ')}</span>
                    </span>
                    <strong className="tabular">{money(s.amount)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">{t('bud.nothing_spent')}</p>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Changes */

function Changes({ data }: { data: Overview }) {
  const name = (id: string | null) => {
    const l = data.lines.find((x) => x.id === id);
    return l ? lineName(l) : null;
  };
  if (!data.changes.length) return <EmptyState icon="transfer" title={t('bud.no_changes')} body={data.year.state === 'draft' ? t('bud.no_changes_draft') : t('bud.no_changes_body')} />;
  return (
    <div className="list-card" role="list">
      {data.changes.map((c) => (
        <div key={c.id} role="listitem" className="list-row">
          <span className="list-icon" aria-hidden="true">
            <Icon name={c.from_line && c.to_line ? 'transfer' : c.to_line ? 'up' : 'down'} size={18} />
          </span>
          <span className="list-main">
            <span className="list-title">
              {c.from_line && c.to_line
                ? t('bud.ch_transfer', { from: name(c.from_line)!, to: name(c.to_line)! })
                : c.to_line
                  ? t('bud.ch_more', { line: name(c.to_line)! })
                  : t('bud.ch_less', { line: name(c.from_line)! })}
            </span>
            <span className="list-sub">{[`#${c.number}`, c.reason, day(c.date), c.by_name].filter(Boolean).join(' · ')}</span>
          </span>
          <strong className="tabular">{money(c.amount)}</strong>
        </div>
      ))}
    </div>
  );
}

function ChangeDialog({ data, onClose, onDone }: { data: Overview; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const usable = data.lines.filter((l) => l.active);
  const [kind, setKind] = useState<'transfer' | 'more' | 'less'>('transfer');
  const [from, setFrom] = useState(usable[0]?.id ?? '');
  const [to, setTo] = useState(usable[1]?.id ?? usable[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const minor = parseMoney(amount);
  const fromLine = usable.find((l) => l.id === from);
  const errors = {
    amount: minor === null || minor <= 0 ? t('bud.amount_wrong') : kind !== 'more' && fromLine && minor > fromLine.available ? t('bud.too_much', { amount: money(Math.max(0, fromLine.available)) }) : null,
    reason: reason.trim().length < 3 ? t('bud.reason_needed') : null,
    same: kind === 'transfer' && from === to ? t('bud.same_line') : null,
  };
  const save = () => {
    setTried(true);
    if (errors.amount || errors.reason || errors.same) return;
    void run(async () => {
      await action('budget.change', { year: data.year.id, from_line: kind === 'more' ? null : from, to_line: kind === 'less' ? null : to, amount: minor, reason: reason.trim() });
      await onDone();
    }, t('bud.change_done'));
  };
  const picker = (value: string, set: (v: string) => void, label: string, hint?: string, error?: string | null) => (
    <Field label={label} hint={hint} error={error}>
      <select className="input" value={value} onChange={(e) => set(e.target.value)}>
        {usable.map((l) => (
          <option key={l.id} value={l.id}>
            {lineName(l)} · {money(l.available)}
          </option>
        ))}
      </select>
    </Field>
  );
  return (
    <Dialog
      title={t('bud.change')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('bud.change_save')}
          </Button>
        </>
      }
    >
      <div className="segmented" role="radiogroup" aria-label={t('bud.change_kind')}>
        {(['transfer', 'more', 'less'] as const).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
            <Icon name={k === 'transfer' ? 'transfer' : k === 'more' ? 'up' : 'down'} size={16} />
            {t(`bud.kind_${k}` as MessageKey)}
          </button>
        ))}
      </div>
      <p className="muted">{t(`bud.kind_${kind}_hint` as MessageKey)}</p>
      {kind !== 'more' ? picker(from, setFrom, kind === 'transfer' ? t('bud.from_line') : t('bud.c_line'), t('bud.available_shown')) : null}
      {kind !== 'less' ? picker(to, setTo, kind === 'transfer' ? t('bud.to_line') : t('bud.c_line'), undefined, tried ? errors.same : null) : null}
      <Field label={t('bud.amount')} error={tried ? errors.amount : null} required>
        <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
      </Field>
      <Field label={t('bud.reason')} hint={t('bud.reason_hint')} error={tried ? errors.reason : null} required>
        <input className="input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Lines and the year */

function LineDialog({ data, line, onClose, onDone }: { data: Overview; line: LineRow | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const y = data.year;
  const editable = data.can.manage && y.state !== 'closed';
  const draft = y.state === 'draft';
  const [code, setCode] = useState(line?.code ?? '');
  const [name, setName] = useState(line?.name ?? '');
  const [group, setGroup] = useState(line?.group ?? '');
  const [amount, setAmount] = useState(line ? (line.original / 100).toFixed(2).replace(/\.00$/, '') : '');
  const [tried, setTried] = useState(false);
  const minor = amount.trim() === '' ? 0 : parseMoney(amount);
  const groups = [...new Set(data.lines.map((l) => l.group).filter(Boolean))] as string[];
  const errors = { code: code.trim() ? null : t('form.required'), name: name.trim() ? null : t('form.required'), amount: minor === null || minor < 0 ? t('bud.amount_wrong') : null };
  const save = (active = line?.active ?? true) => {
    setTried(true);
    if (errors.code || errors.name || errors.amount) return;
    void run(async () => {
      await action('budget.save_line', { ...(line ? { id: line.id } : {}), year: y.id, code: code.trim(), name: name.trim(), group: group.trim() || null, original: draft ? minor : (line?.original ?? 0), active });
      await onDone();
    }, t('bud.line_saved'));
  };
  const months = line ? Array.from({ length: 12 }, (_, i) => data.months.find((m) => m.line === line.id && m.month === i + 1)?.amount ?? 0) : [];
  const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', timeZone: 'UTC' });
  return (
    <Dialog
      title={line ? lineName(line) : t('bud.add_line')}
      onClose={onClose}
      footer={
        editable ? (
          <>
            {line ? (
              <Button variant="ghost" icon={line.active ? 'pause' : 'undo'} className="dialog-left" onClick={() => save(!line.active)} disabled={busy}>
                {line.active ? t('bud.put_away') : t('bud.use_again')}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => save()} busy={busy}>
              {t('common.save')}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        )
      }
    >
      {line ? (
        <div className="item-summary">
          <div>
            <span className="muted">{t('bud.c_current')}</span>
            <strong>{money(line.current)}</strong>
          </div>
          <div>
            <span className="muted">{t('bud.c_spent')}</span>
            <strong>{money(line.spent)}</strong>
          </div>
          <div>
            <span className="muted">{t('bud.c_committed')}</span>
            <strong>{money(line.committed)}</strong>
          </div>
          <div>
            <span className="muted">{t('bud.c_available')}</span>
            <strong className={line.available < 0 ? 'text-danger' : ''}>{money(line.available)}</strong>
          </div>
        </div>
      ) : null}
      {editable ? (
        <>
          <div className="form-row">
            <Field label={t('bud.c_code')} hint={t('bud.code_hint')} error={tried ? errors.code : null} required>
              <input className="input tabular" maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} data-autofocus />
            </Field>
            <Field label={t('bud.c_group')} hint={t('bud.group_hint')}>
              <input className="input" maxLength={80} list="bud-groups" value={group} onChange={(e) => setGroup(e.target.value)} />
              <datalist id="bud-groups">
                {groups.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </Field>
          </div>
          <Field label={t('bud.c_name')} error={tried ? errors.name : null} required>
            <input className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('bud.c_original')} hint={draft ? t('bud.original_hint') : t('bud.original_locked')} error={tried ? errors.amount : null}>
            <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" disabled={!draft} />
          </Field>
        </>
      ) : null}
      {line && months.some(Boolean) ? (
        <section aria-label={t('bud.by_month')}>
          <h3 className="section-title">{t('bud.by_month')}</h3>
          <Bars small items={months.map((v, i) => { const label = fmt.format(new Date(Date.UTC(y.year, i, 1))); return { key: String(i), label, value: v, tip: `${label}: ${money(v)}` }; })} />
        </section>
      ) : null}
    </Dialog>
  );
}

function YearSettings({ data, onClose, onDone }: { data: Overview; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const y = data.year;
  const [control, setControl] = useState<Control>(y.control);
  const [note, setNote] = useState(y.note ?? '');
  const [payroll, setPayroll] = useState(y.payroll_line ?? '');
  const save = () =>
    void run(async () => {
      await action('budget.save_year', { id: y.id, year: y.year, control, note: note.trim() || null, payroll_line: payroll || null });
      await onDone();
    }, t('bud.settings_saved'));
  return (
    <Dialog
      title={t('bud.settings_title', { year: y.year })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={save} busy={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <ControlPicker value={control} onChange={setControl} />
      {data.sources.payroll ? (
        <Field label={t('bud.payroll_line')} hint={t('bud.payroll_line_hint')}>
          <select className="input" value={payroll} onChange={(e) => setPayroll(e.target.value)}>
            <option value="">{t('bud.payroll_none')}</option>
            {data.lines
              .filter((l) => l.active)
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {lineName(l)}
                </option>
              ))}
          </select>
        </Field>
      ) : null}
      <Field label={t('bud.note')} hint={t('bud.note_hint')}>
        <textarea className="input" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}

function ControlPicker({ value, onChange }: { value: Control; onChange: (c: Control) => void }) {
  return (
    <div className="field">
      <span className="field-label" id="bud-control">
        {t('bud.control')}
      </span>
      <div className="bud-controls" role="radiogroup" aria-labelledby="bud-control">
        {(['off', 'warn', 'strict'] as const).map((c) => (
          <button key={c} type="button" role="radio" aria-checked={value === c} className={`bud-control${value === c ? ' active' : ''}`} onClick={() => onChange(c)}>
            <Icon name={c === 'off' ? 'eye' : c === 'warn' ? 'alert' : 'shieldCheck'} size={18} />
            <span>
              <span className="strong">{t(`bud.control_${c}` as MessageKey)}</span>
              <span className="muted small">{t(`bud.control_${c}_hint` as MessageKey)}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NewYear({ years, suggested, onClose, onDone }: { years: YearRow[]; suggested: number; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [year, setYear] = useState(String(suggested));
  const [control, setControl] = useState<Control>('warn');
  const [copy, setCopy] = useState(years[0]?.id ?? '');
  const [tried, setTried] = useState(false);
  const n = Number(year);
  const error = !/^\d{4}$/.test(year) || n < 2000 || n > 2100 ? t('bud.year_wrong') : years.some((y) => y.year === n) ? t('bud.year_exists', { year: n }) : null;
  const save = () => {
    setTried(true);
    if (error) return;
    void run(async () => {
      const y = await action<{ id: string }>('budget.save_year', { year: n, control, copy_from: copy || null });
      await onDone(y.id);
    }, t('bud.year_created', { year: n }));
  };
  return (
    <Dialog
      title={t('bud.new_year')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="plus" onClick={save} busy={busy}>
            {t('bud.create_year')}
          </Button>
        </>
      }
    >
      <Field label={t('bud.year')} error={tried ? error : null} required>
        <input className="input tabular" inputMode="numeric" maxLength={4} value={year} onChange={(e) => setYear(e.target.value)} data-autofocus />
      </Field>
      {years.length ? (
        <Field label={t('bud.copy_from')} hint={t('bud.copy_from_hint')}>
          <select className="input" value={copy} onChange={(e) => setCopy(e.target.value)}>
            <option value="">{t('bud.copy_none')}</option>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {t('bud.year_n', { year: y.year })} · {tn('bud.n_lines', y.lines)}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <ControlPicker value={control} onChange={setControl} />
    </Dialog>
  );
}
